import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { GraduationCap, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const { loginWithPassword } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msError, setMsError] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("ms_error") === "unregistered") {
      const em = q.get("email") || "your Microsoft account";
      setMsError(`${em} is not registered on this school. Please contact your admin.`);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await loginWithPassword(email, password);
      toast.success(`Welcome, ${u.name}`);
      const path = u.open_school ? "/open" : u.role === "global_admin" ? "/global" : `/${u.role}`;
      nav(path, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const azure = async () => {
    try {
      const r = await api.get("/auth/microsoft/sso-start");
      if (r.data?.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        toast.info("Microsoft SSO not enabled yet.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Microsoft SSO not enabled yet.";
      toast.info(detail);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-[#C4B5FD] border-r-2 border-black p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="back-home-link">
          <GraduationCap size={28} strokeWidth={2.5}/>
          <span className="display text-2xl font-bold">Ponderr Schoolz</span>
        </Link>
        <div>
          <h2 className="display text-5xl font-black leading-[1.05]">
            Sign in to your <span className="bg-[#FDE047] px-2 border-2 border-black inline-block">classroom</span>.
          </h2>
          <p className="mt-4 max-w-md text-slate-800">
            Admin, educator, student dashboards — one login, role-aware UI.
          </p>
        </div>
        <div className="text-xs mono opacity-70">v1.0 · MOCKED SMS · Azure SSO (v2)</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#FBF9F6]">
        <div className="nb-card p-8 w-full max-w-md">
          <h1 className="display text-3xl font-bold">Welcome back</h1>
          <p className="text-sm text-slate-600 mb-6">Sign in to continue</p>

          {msError && (
            <div className="mb-4 nb-card-sm p-3 bg-[#FCA5A5] text-sm" data-testid="ms-error-banner">
              {msError}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="nb-label">Email</label>
              <input data-testid="login-email" className="nb-input mt-1" type="email" required
                     value={email} onChange={e=>setEmail(e.target.value)} />
            </div>
            <div>
              <label className="nb-label">Password</label>
              <input data-testid="login-password" className="nb-input mt-1" type="password" required
                     value={password} onChange={e=>setPassword(e.target.value)} />
            </div>
            <button data-testid="login-submit" disabled={busy} className="nb-btn w-full justify-center">
              {busy? "Signing in..." : "Sign In"} <ArrowRight size={16}/>
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-[2px] bg-black"/>
            <span className="nb-label">OR</span>
            <div className="flex-1 h-[2px] bg-black"/>
          </div>

          <div className="space-y-3">
            <button data-testid="google-login-btn" onClick={google} className="nb-btn nb-btn-ghost w-full justify-center">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4"/> Continue with Google
            </button>
            <button data-testid="azure-login-btn" onClick={azure} className="nb-btn nb-btn-info w-full justify-center">
              <img alt="" className="w-4 h-4" src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='10' height='10' x='1' y='1' fill='%23F25022'/><rect width='10' height='10' x='13' y='1' fill='%237FBA00'/><rect width='10' height='10' x='1' y='13' fill='%2300A4EF'/><rect width='10' height='10' x='13' y='13' fill='%23FFB900'/></svg>"/>
              Continue with Microsoft
            </button>
          </div>

          <div className="mt-6 nb-card-sm p-3 bg-[#DDD6FE] text-sm flex items-center justify-between gap-2">
            <span className="font-bold">New to Ponderr?</span>
            <Link to="/open-school/onboard" className="nb-btn nb-btn-secondary text-xs" data-testid="login-join-open-school">
              Join Open School →
            </Link>
          </div>

          <div className="mt-4 text-xs text-slate-600 nb-card-sm p-3 bg-[#FDE047]">
            <div className="font-bold mb-1">DEMO LOGINS</div>
            <div>anika@school.edu / edu123 (Teacher)</div>
            <div>aarav@school.edu / stu123 (Student)</div>
            <div className="mt-1 opacity-70">Admin access requires your real credentials.</div>
          </div>
          <Link to="/" className="nb-btn nb-btn-ghost w-full justify-center mt-4" data-testid="back-to-landing">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
